import { useAccountsService } from '@restworld/data-services';
import { AxiosResponse } from 'axios';
import { useCallback, useRef, useState } from 'react';
import { useSetAtom } from 'jotai';
import { useAuthContext } from '@restworld/feature-authentication';
import { useTranslation } from 'next-i18next';
import { useAnalytics } from '@restworld/utils-common';
import {
  ContentType as ModalContentType,
  closeAuthFlowModal,
  closeEmailTokenSentModal,
  setActiveContent as setActiveModalContent,
  updateEmailVerificationDetails,
} from '../../../context/auth-flow';

export type UseEmailOtpFormProps = {
  email: string;
  isModal?: boolean;
};

/**
 * Verifies the 6-digit code emailed alongside the magic link. Mirrors
 * useOTPForm (phone) but posts { email, otp_code } to /accounts/login and closes
 * the email-token-sent modal on success. Exposes `verify(code)` so the caller
 * can auto-submit from OTPInput's onChange once the code reaches 6 digits, no
 * button click required. Owns the typed code, like useOTPForm owns its form value,
 * so a failed verify can clear it.
 */
export function useEmailOtpForm({ email, isModal = true }: UseEmailOtpFormProps) {
  const [loading, setLoading] = useState(false);
  const [serverValidationFailed, setServerValidationFailed] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  // Too many wrong codes: the backend has revoked this person's code and link and answers 429 to
  // every further try until a new email is requested, so the form stops submitting codes.
  const [tooManyAttempts, setTooManyAttempts] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [otpValue, setOtpValue] = useState('');
  // Guards against a double submit (auto-submit on complete + a button click).
  const inFlightRef = useRef(false);

  const acc = useAccountsService();
  const setActiveContent = useSetAtom(setActiveModalContent);
  const closeModal = useSetAtom(closeAuthFlowModal);
  const onCloseEmailTokenSentModal = useSetAtom(closeEmailTokenSentModal);
  const resetEmailDetails = useSetAtom(updateEmailVerificationDetails);
  const authContext = useAuthContext();
  const { t } = useTranslation('auth-flow');
  const { trackEvent } = useAnalytics();

  const closeTimeout = () => new Promise((resolve) => setTimeout(resolve, 1000));

  const verify = useCallback(
    async (otpCode: string) => {
      if (inFlightRef.current || otpCode.length !== 6 || tooManyAttempts) return;
      inFlightRef.current = true;
      setLoading(true);
      setServerValidationFailed(false);
      setErrorMessage(undefined);
      try {
        const response: AxiosResponse = await acc.verifyEmailOtp({ email, otpCode });
        const jwtToken = response?.data?.jwt || undefined;
        setIsSuccess(true);
        if (authContext?.verifyToken) {
          authContext.initializeAuthentication(jwtToken);
          trackEvent('complete_signin', {
            method: 'email_otp',
            email,
            user_id: authContext?.user?.id,
          });
        }
        await closeTimeout();
        // Clear the persisted send so a later sign-in starts fresh (the code was
        // just consumed) instead of resuming this now-dead code step.
        resetEmailDetails({
          email: '',
          waitTime: 0,
          tokenValidityTime: 0,
          tokenExpiresAt: undefined,
          waitExpiresAt: undefined,
        });
        setActiveContent(ModalContentType.NO_CONTENT);
        if (isModal) {
          onCloseEmailTokenSentModal();
          closeModal();
        }
      } catch (error) {
        setIsSuccess(false);
        // Cleared, as on the phone screen, so the next code is typed from the first cell.
        setOtpValue('');
        setServerValidationFailed(true);
        // The axios interceptor rejects with `{ code: status, message, data }`, not an AxiosError.
        const locked = (error as { code?: number })?.code === 429;
        setTooManyAttempts(locked);
        setErrorMessage(t(locked ? 'login_otp_too_many_attempts' : 'email_login_otp_validation_failed'));
      } finally {
        setLoading(false);
        inFlightRef.current = false;
      }
    },
    [
      acc,
      email,
      tooManyAttempts,
      authContext,
      trackEvent,
      setActiveContent,
      isModal,
      onCloseEmailTokenSentModal,
      closeModal,
      resetEmailDetails,
      t,
    ]
  );

  return {
    loading,
    isSuccess,
    serverValidationFailed,
    setServerValidationFailed,
    tooManyAttempts,
    setTooManyAttempts,
    otpValue,
    setOtpValue,
    errorMessage,
    verify,
  };
}
